"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { BadgeCheck, ExternalLink, Share2, Link2 } from "lucide-react";
import { useTenant } from "@/components/tenant/TenantProvider";
import { formatRupiah } from "@/lib/utils";
import { toast } from "@/components/ui/toast";
import SafeImage from "@/components/common/SafeImage";
import SandboxedHtmlFrame from "@/components/common/SandboxedHtmlFrame";
import { RICH_HTML_CLASS } from "@/components/landing/types";
import { SOCIAL_BRAND_ICONS, LINK_ICON_MAP, GlobeIcon } from "./BrandIcons";
import { sanitizeLessonHtml } from "@growwithai/shared";

// Bentuk payload dari GET /api/public/store (resolusi server).
export interface StoreCard {
  kind: "course" | "digital_product" | "bundle";
  slug: string;
  title: string;
  thumbnail_url: string | null;
  price: number | null;
  compare_at_price: number | null;
  pricing_mode?: string | null;
  href: string;
  badge: string;
}
export type StoreBlockData = {
  type: "product_section";
  id: string;
  heading: string;
  subheading?: string | null;
  layout?: "grid" | "carousel";
  items: StoreCard[];
} | {
  type: "link";
  id: string;
  label: string;
  url: string;
  icon?: string;
  description?: string | null;
} | {
  type: "socials";
  id: string;
  title?: string | null;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  x?: string;
  facebook?: string;
  website?: string;
} | {
  type: "image";
  id: string;
  image_url: string;
  href?: string | null;
  caption?: string | null;
} | {
  type: "text";
  id: string;
  body_html: string;
} | {
  type: "embed";
  id: string;
  html: string;
  height_mode?: "auto" | "fixed";
  height_px?: number;
};
export type StoreButtonStyle = "glass" | "solid" | "outline";

/** Tata letak halaman toko — independen dari button_style/accent_color.
 *  mosaic_grid = grid bento media-padat (ala Beacons); banner_hero = cover
 *  full-bleed dominan dengan avatar menumpuk (ala Lynk.id). */
export type StoreLayoutVariant = "classic" | "minimal_list" | "grid_card" | "bio_center" | "mosaic_grid" | "banner_hero";

/** Layer dekoratif latar — dibangun dari --st-accent, independen dari accent_color. */
export type StoreBackgroundStyle = "flat" | "gradient" | "pattern_dots" | "pattern_grid";
export interface StorePayload {
  enabled: boolean;
  as_home?: boolean;
  title?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  cover_url?: string | null;
  verified?: boolean;
  button_style?: StoreButtonStyle;
  layout_variant?: StoreLayoutVariant;
  background_style?: StoreBackgroundStyle;
  accent_color?: string | null;
  blocks: StoreBlockData[];
}
function priceLabel(card: StoreCard): string {
  if (card.price == null || card.price === 0) return "Gratis";
  const prefix = card.pricing_mode === "pwyw" ? "Mulai " : "";
  return prefix + formatRupiah(card.price);
}
function discountPct(card: StoreCard): number | null {
  if (card.price == null || card.price <= 0) return null;
  if (card.compare_at_price == null || card.compare_at_price <= card.price) return null;
  return Math.round((1 - card.price / card.compare_at_price) * 100);
}
function ProductCard(t0) {
  const $ = _c(32);
  const {
    card
  } = t0;
  let t1;
  if ($[0] !== card) {
    t1 = discountPct(card);
    $[0] = card;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const off = t1;
  const t2 = card.href;
  let t3;
  if ($[2] !== card.thumbnail_url || $[3] !== card.title) {
    t3 = card.thumbnail_url ? <SafeImage src={card.thumbnail_url} alt={card.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" /> : <div className="flex h-full w-full items-center justify-center text-3xl font-bold opacity-40">{card.title.charAt(0).toUpperCase()}</div>;
    $[2] = card.thumbnail_url;
    $[3] = card.title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== card.badge) {
    t4 = <span className="store-badge">{card.badge}</span>;
    $[5] = card.badge;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== off) {
    t5 = off != null && <span className="store-discount">-{off}%</span>;
    $[7] = off;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = <div className="store-card__media">{t3}{t4}{t5}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== card.title) {
    t7 = <h3 className="store-card__title line-clamp-2 text-sm font-semibold">{card.title}</h3>;
    $[13] = card.title;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== card) {
    t8 = priceLabel(card);
    $[15] = card;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <span className="text-[15px] font-bold">{t8}</span>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== card.compare_at_price || $[20] !== card.price) {
    t10 = card.compare_at_price != null && card.compare_at_price > (card.price ?? 0) && <span className="store-strike text-xs line-through">{formatRupiah(card.compare_at_price)}</span>;
    $[19] = card.compare_at_price;
    $[20] = card.price;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <div className="mt-auto flex items-baseline gap-2 pt-3">{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t7) {
    t12 = <div className="flex flex-1 flex-col p-3.5">{t7}{t11}</div>;
    $[25] = t11;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== card.href || $[29] !== t12 || $[30] !== t6) {
    t13 = <Link href={t2} className="store-card group">{t6}{t12}</Link>;
    $[28] = card.href;
    $[29] = t12;
    $[30] = t6;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}
function normalizeSocialUrl(platform: string, value: string): string {
  const v = value.trim();
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, "");
  switch (platform) {
    case "instagram":
      return `https://instagram.com/${handle}`;
    case "tiktok":
      return `https://tiktok.com/@${handle}`;
    case "youtube":
      return `https://youtube.com/@${handle}`;
    case "x":
      return `https://x.com/${handle}`;
    case "facebook":
      return `https://facebook.com/${handle}`;
    default:
      return `https://${handle}`;
  }
}
function SocialRow(t0) {
  const $ = _c(4);
  const {
    block,
    centered: t1
  } = t0;
  const centered = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== block || $[1] !== centered) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const entries = (["instagram", "tiktok", "youtube", "x", "facebook", "website"] as const).map(k => [k, block[k]] as const).filter(_temp);
      if (entries.length === 0) {
        t3 = null;
        break bb0;
      }
      t2 = <div className={`flex flex-wrap items-center gap-2.5 ${centered ? "justify-center" : ""}`}>{entries.map(_temp2)}</div>;
    }
    $[0] = block;
    $[1] = centered;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  return t2;
}

/**
 * `minimal_list` butuh susunan horizontal (avatar kiri, judul+bio kanan) —
 * beda struktural dari susunan tumpuk-tengah yang dipakai 3 varian lain, jadi
 * tidak bisa CSS-only tanpa mengandalkan nama kelas Tailwind sebagai selektor.
 * `bio_center` cukup CSS (avatar/padding lebih besar) di atas struktur yang sama.
 */
function _temp2(t0) {
  const [platform, value] = t0;
  const Icon = SOCIAL_BRAND_ICONS[platform] ?? GlobeIcon;
  return <a key={platform} href={normalizeSocialUrl(platform, value as string)} target="_blank" rel="noopener noreferrer" aria-label={platform} className="store-social"><Icon className="h-[18px] w-[18px]" /></a>;
}
function _temp(t0) {
  const [, v] = t0;
  return typeof v === "string" && v.trim() !== "";
}
function StoreHeader(t0) {
  const $ = _c(42);
  const {
    store,
    title,
    avatar,
    layout
  } = t0;
  if (layout === "minimal_list") {
    const t1 = `store-header store-header--flat store-enter ${store.cover_url ? "store-header--cover" : ""}`;
    let t2;
    if ($[0] !== avatar || $[1] !== title) {
      t2 = <div className="store-avatar store-avatar--sm shrink-0">{avatar ? <SafeImage src={avatar} alt={title} loading="lazy" className="h-full w-full rounded-full object-cover" /> : <span className="flex h-full w-full items-center justify-center rounded-full text-lg font-bold text-white" style={{
          background: "var(--st-accent)"
        }}>{title.charAt(0).toUpperCase()}</span>}</div>;
      $[0] = avatar;
      $[1] = title;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== title) {
      t3 = <h1 className="store-heading truncate text-lg font-bold">{title}</h1>;
      $[3] = title;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== store.verified) {
      t4 = store.verified && <BadgeCheck className="h-4 w-4 shrink-0" style={{
        color: "var(--st-accent)"
      }} aria-label="Terverifikasi" />;
      $[5] = store.verified;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t3 || $[8] !== t4) {
      t5 = <div className="flex items-center gap-1.5">{t3}{t4}</div>;
      $[7] = t3;
      $[8] = t4;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== store.bio) {
      t6 = store.bio && <p className="store-muted mt-0.5 line-clamp-2 text-sm leading-relaxed">{store.bio}</p>;
      $[10] = store.bio;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== t5 || $[13] !== t6) {
      t7 = <div className="min-w-0">{t5}{t6}</div>;
      $[12] = t5;
      $[13] = t6;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== t2 || $[16] !== t7) {
      t8 = <div className="flex items-center gap-3.5 text-left">{t2}{t7}</div>;
      $[15] = t2;
      $[16] = t7;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== t1 || $[19] !== t8) {
      t9 = <header className={t1}>{t8}</header>;
      $[18] = t1;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    return t9;
  }
  const isBio = layout === "bio_center";
  const t1 = `store-header store-glass store-enter ${isBio ? "store-header--bio" : ""} ${store.cover_url ? "store-header--cover" : ""}`;
  const t2 = `store-avatar ${isBio ? "store-avatar--lg" : ""}`;
  let t3;
  if ($[21] !== avatar || $[22] !== title) {
    t3 = avatar ? <SafeImage src={avatar} alt={title} loading="lazy" className="h-full w-full rounded-full object-cover" /> : <span className="flex h-full w-full items-center justify-center rounded-full text-2xl font-bold text-white" style={{
      background: "var(--st-accent)"
    }}>{title.charAt(0).toUpperCase()}</span>;
    $[21] = avatar;
    $[22] = title;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== t2 || $[25] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  let t5;
  if ($[27] !== title) {
    t5 = <h1 className="store-heading text-xl font-bold">{title}</h1>;
    $[27] = title;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== store.verified) {
    t6 = store.verified && <BadgeCheck className="h-5 w-5 shrink-0" style={{
      color: "var(--st-accent)"
    }} aria-label="Terverifikasi" />;
    $[29] = store.verified;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== t5 || $[32] !== t6) {
    t7 = <div className="mt-3 flex items-center justify-center gap-1.5">{t5}{t6}</div>;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== isBio || $[35] !== store.bio) {
    t8 = store.bio && <p className={`store-muted mx-auto mt-2 max-w-md text-sm leading-relaxed ${isBio ? "text-base" : ""}`}>{store.bio}</p>;
    $[34] = isBio;
    $[35] = store.bio;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t1 || $[38] !== t4 || $[39] !== t7 || $[40] !== t8) {
    t9 = <header className={t1}>{t4}{t7}{t8}</header>;
    $[37] = t1;
    $[38] = t4;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  return t9;
}
function Block(t0) {
  const $ = _c(54);
  const {
    block,
    buttonStyle
  } = t0;
  switch (block.type) {
    case "product_section":
      {
        if (block.items.length === 0) {
          return null;
        }
        let t1;
        if ($[0] !== block.heading) {
          t1 = <h2 className="store-heading text-lg font-bold">{block.heading}</h2>;
          $[0] = block.heading;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        let t2;
        if ($[2] !== block.subheading) {
          t2 = block.subheading && <p className="store-muted mt-0.5 text-sm">{block.subheading}</p>;
          $[2] = block.subheading;
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        const t3 = block.layout === "carousel" ? "store-carousel mt-4 flex snap-x gap-4 overflow-x-auto pb-2 [&>a]:w-[15rem] [&>a]:shrink-0 [&>a]:snap-start" : "mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3";
        let t4;
        if ($[4] !== block.items) {
          t4 = block.items.map(_temp3);
          $[4] = block.items;
          $[5] = t4;
        } else {
          t4 = $[5];
        }
        let t5;
        if ($[6] !== t3 || $[7] !== t4) {
          t5 = <div className={t3}>{t4}</div>;
          $[6] = t3;
          $[7] = t4;
          $[8] = t5;
        } else {
          t5 = $[8];
        }
        let t6;
        if ($[9] !== t1 || $[10] !== t2 || $[11] !== t5) {
          t6 = <section className="w-full">{t1}{t2}{t5}</section>;
          $[9] = t1;
          $[10] = t2;
          $[11] = t5;
          $[12] = t6;
        } else {
          t6 = $[12];
        }
        return t6;
      }
    case "link":
      {
        const LinkIcon = block.icon && LINK_ICON_MAP[block.icon] || Link2;
        const t1 = `store-link store-link--${buttonStyle}`;
        let t2;
        if ($[13] !== LinkIcon) {
          t2 = <span className="store-link__icon"><LinkIcon className="h-[18px] w-[18px]" /></span>;
          $[13] = LinkIcon;
          $[14] = t2;
        } else {
          t2 = $[14];
        }
        let t3;
        if ($[15] !== block.label) {
          t3 = <span className="block truncate">{block.label}</span>;
          $[15] = block.label;
          $[16] = t3;
        } else {
          t3 = $[16];
        }
        let t4;
        if ($[17] !== block.description) {
          t4 = block.description && <span className="store-link__desc block truncate text-xs font-normal">{block.description}</span>;
          $[17] = block.description;
          $[18] = t4;
        } else {
          t4 = $[18];
        }
        let t5;
        if ($[19] !== t3 || $[20] !== t4) {
          t5 = <span className="min-w-0 flex-1">{t3}{t4}</span>;
          $[19] = t3;
          $[20] = t4;
          $[21] = t5;
        } else {
          t5 = $[21];
        }
        let t6;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <ExternalLink className="h-4 w-4 shrink-0 opacity-50" />;
          $[22] = t6;
        } else {
          t6 = $[22];
        }
        let t7;
        if ($[23] !== block.url || $[24] !== t1 || $[25] !== t2 || $[26] !== t5) {
          t7 = <a href={block.url} target="_blank" rel="noopener noreferrer" className={t1}>{t2}{t5}{t6}</a>;
          $[23] = block.url;
          $[24] = t1;
          $[25] = t2;
          $[26] = t5;
          $[27] = t7;
        } else {
          t7 = $[27];
        }
        return t7;
      }
    case "socials":
      {
        let t1;
        if ($[28] !== block.title) {
          t1 = block.title && <h2 className="store-muted mb-3 text-sm font-semibold">{block.title}</h2>;
          $[28] = block.title;
          $[29] = t1;
        } else {
          t1 = $[29];
        }
        let t2;
        if ($[30] !== block) {
          t2 = <SocialRow block={block} />;
          $[30] = block;
          $[31] = t2;
        } else {
          t2 = $[31];
        }
        let t3;
        if ($[32] !== t1 || $[33] !== t2) {
          t3 = <section className="w-full text-center">{t1}{t2}</section>;
          $[32] = t1;
          $[33] = t2;
          $[34] = t3;
        } else {
          t3 = $[34];
        }
        return t3;
      }
    case "image":
      {
        const t1 = block.caption ?? "";
        let t2;
        if ($[35] !== block.image_url || $[36] !== t1) {
          t2 = <SafeImage src={block.image_url} alt={t1} loading="lazy" className="w-full rounded-2xl" />;
          $[35] = block.image_url;
          $[36] = t1;
          $[37] = t2;
        } else {
          t2 = $[37];
        }
        const img = t2;
        let t3;
        if ($[38] !== block.href || $[39] !== img) {
          t3 = block.href ? <a href={block.href} target="_blank" rel="noopener noreferrer">{img}</a> : img;
          $[38] = block.href;
          $[39] = img;
          $[40] = t3;
        } else {
          t3 = $[40];
        }
        let t4;
        if ($[41] !== block.caption) {
          t4 = block.caption && <figcaption className="store-muted px-1 py-2 text-center text-xs">{block.caption}</figcaption>;
          $[41] = block.caption;
          $[42] = t4;
        } else {
          t4 = $[42];
        }
        let t5;
        if ($[43] !== t3 || $[44] !== t4) {
          t5 = <figure className="store-media w-full overflow-hidden rounded-2xl">{t3}{t4}</figure>;
          $[43] = t3;
          $[44] = t4;
          $[45] = t5;
        } else {
          t5 = $[45];
        }
        return t5;
      }
    case "text":
      {
        let t1;
        if ($[46] !== block.body_html) {
          t1 = sanitizeLessonHtml(block.body_html);
          $[46] = block.body_html;
          $[47] = t1;
        } else {
          t1 = $[47];
        }
        let t2;
        if ($[48] !== t1) {
          t2 = <div className={`store-glass w-full rounded-2xl p-5 ${RICH_HTML_CLASS}`} dangerouslySetInnerHTML={{
            __html: t1
          }} />;
          $[48] = t1;
          $[49] = t2;
        } else {
          t2 = $[49];
        }
        return t2;
      }
    case "embed":
      {
        const t1 = block.height_mode ?? "auto";
        let t2;
        if ($[50] !== block.height_px || $[51] !== block.html || $[52] !== t1) {
          t2 = <div className="store-media w-full overflow-hidden rounded-2xl"><SandboxedHtmlFrame html={block.html} heightMode={t1} heightPx={block.height_px} /></div>;
          $[50] = block.height_px;
          $[51] = block.html;
          $[52] = t1;
          $[53] = t2;
        } else {
          t2 = $[53];
        }
        return t2;
      }
    default:
      {
        return null;
      }
  }
}
function _temp3(card) {
  return <ProductCard key={`${card.kind}:${card.slug}`} card={card} />;
}
function ShareButton({
  title
}: {
  title: string;
}) {
  const onShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({
          title,
          url
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link etalase tersalin");
    } catch {
      /* user membatalkan share — abaikan */
    }
  };
  return <button type="button" onClick={onShare} aria-label="Bagikan etalase" className="store-share">
      <Share2 className="h-[18px] w-[18px]" />
      <span className="hidden sm:inline">Bagikan</span>
    </button>;
}
export default function StoreRenderer({
  store
}: {
  store: StorePayload;
}) {
  const branding = useTenant();
  const title = store.title || branding.brand_name;
  const avatar = store.avatar_url || branding.logo_url;
  const buttonStyle: StoreButtonStyle = store.button_style ?? "glass";
  const layout: StoreLayoutVariant = store.layout_variant ?? "classic";
  const backgroundStyle: StoreBackgroundStyle = store.background_style ?? "flat";
  return <div className={`store-root store-root--${layout}`} style={{
    // Aksen store: override admin (hex) atau jatuh ke aksen brand tenant.
    // TANPA literal hijau sebagai fallback — `#16a34a` yang dulu di sini bukan
    // warna brand bawaan (itu #0F6E56), jadi tiap tenant tanpa aksen kustom
    // mendapat hijau asing. --brand-primary selalu ada dari globals.css.
    ["--st-accent" as string]: store.accent_color || "var(--brand-primary)",
    fontFamily: "var(--brand-font, inherit)"
  }}>
      <style>{STORE_CSS}</style>
      <div className={`store-bg ${backgroundStyle !== "flat" ? `store-bg--${backgroundStyle.replace(/_/g, "-")}` : ""}`} aria-hidden="true" />

      <div className="store-container">
        {store.cover_url && <div className="store-cover store-enter">
            {/* Kandidat LCP utama etalase (tampil di atas lipatan) → eager + fetchPriority high. */}
            <SafeImage src={store.cover_url} alt="" loading="eager" fetchPriority="high" className="h-full w-full object-cover" />
          </div>}

        <StoreHeader store={store} title={title} avatar={avatar} layout={layout} />

        <main id="main-content" tabIndex={-1} className="store-blocks">
          {store.blocks.map((block, i) => <div key={block.id} className="store-enter" style={{
          animationDelay: `${Math.min(i + 1, 8) * 60}ms`
        }}>
              <Block block={block} buttonStyle={buttonStyle} />
            </div>)}
          {store.blocks.length === 0 && <p className="store-muted py-16 text-center text-sm">Etalase belum diisi.</p>}
        </main>

        <footer className="store-footer">
          <Link href="/login" className="store-footer__link">Masuk ke akun</Link>
        </footer>
      </div>

      <ShareButton title={title} />
    </div>;
}

// CSS scoped ke .store-root — theme-aware (light/dark via prefers-color-scheme),
// glassmorphism, glow aksen, dan animasi masuk (dinonaktifkan saat reduce-motion).
const STORE_CSS = `
.store-root{
  --st-bg:#f1f1f5; --st-fg:#141417; --st-muted:#5c5c66; --st-card:#ffffff;
  position:relative; min-height:100vh; overflow-x:hidden; background:var(--st-bg); color:var(--st-fg);
  -webkit-font-smoothing:antialiased;
}
@media (prefers-color-scheme: dark){
  .store-root{ --st-bg:#08080c; --st-fg:#f4f4f7; --st-muted:#9a9aa6; --st-card:#141419; }
}
/* Warna solid, bukan gradien: tiga radial-gradient beraksen sebelumnya di sini
   melanggar aturan bebas-gradien dan tampil hijau tiap kali --st-accent jatuh ke
   nilai bawaan. Satu lapis tint rata memberi nuansa brand yang sama tanpa itu. */
.store-bg{
  position:fixed; inset:0; z-index:0; pointer-events:none;
  background:color-mix(in srgb, var(--st-accent) 4%, transparent);
}
@media (prefers-color-scheme: dark){
  .store-bg{ background:color-mix(in srgb, var(--st-accent) 7%, transparent); }
}
.store-container{ position:relative; z-index:1; margin:0 auto; max-width:44rem; padding:2.25rem 1.25rem calc(6rem + env(safe-area-inset-bottom)); }
.store-glass{
  background:color-mix(in srgb, var(--st-card) 68%, transparent);
  border:1px solid color-mix(in srgb, #ffffff 55%, transparent);
  box-shadow:0 10px 34px rgba(0,0,0,.10), inset 0 1px 0 rgba(255,255,255,.55);
  -webkit-backdrop-filter:blur(18px) saturate(1.5); backdrop-filter:blur(18px) saturate(1.5);
}
@media (prefers-color-scheme: dark){
  .store-glass{
    background:color-mix(in srgb, var(--st-card) 64%, transparent);
    border-color:color-mix(in srgb, #ffffff 12%, transparent);
    box-shadow:0 10px 34px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.06);
  }
}
.store-heading{ color:var(--st-fg); }
.store-muted{ color:var(--st-muted); }
.store-strike{ color:var(--st-muted); }

.store-cover{ position:relative; aspect-ratio:3/1; overflow:hidden; border-radius:1.25rem; box-shadow:0 10px 30px rgba(0,0,0,.14); }
.store-header{ border-radius:1.5rem; padding:1.5rem 1.25rem; text-align:center; }
.store-header--cover{ margin-top:-2.75rem; }
.store-avatar{
  width:5.5rem; height:5.5rem; margin:0 auto; border-radius:9999px;
  box-shadow:0 0 0 4px color-mix(in srgb, var(--st-card) 70%, transparent), 0 6px 18px rgba(0,0,0,.18);
  outline:2px solid color-mix(in srgb, var(--st-accent) 45%, transparent); outline-offset:2px;
}

.store-blocks{ margin-top:1.75rem; display:flex; flex-direction:column; gap:1.5rem; }
.store-footer{ margin-top:2.5rem; text-align:center; padding-top:1.25rem; border-top:1px solid color-mix(in srgb, var(--st-fg) 10%, transparent); }
.store-footer__link{ font-size:.75rem; font-weight:600; color:var(--st-muted); }
.store-footer__link:hover{ color:var(--st-accent); }

/* Tombol link */
.store-link{
  display:flex; align-items:center; gap:.75rem; width:100%; padding:.95rem 1.1rem;
  border-radius:1rem; font-weight:600; color:var(--st-fg); text-decoration:none;
  transition:transform .2s cubic-bezier(.2,.8,.2,1), box-shadow .2s, background .2s, border-color .2s;
}
.store-link:hover{ transform:translateY(-2px); }
.store-link:active{ transform:scale(.98); }
.store-link__icon{ display:flex; align-items:center; justify-content:center; width:2.1rem; height:2.1rem; border-radius:.7rem; background:color-mix(in srgb, var(--st-accent) 16%, transparent); color:var(--st-accent); flex-shrink:0; }
.store-link__desc{ color:var(--st-muted); }
.store-link--glass{
  background:color-mix(in srgb, var(--st-card) 66%, transparent);
  border:1px solid color-mix(in srgb, #ffffff 55%, transparent);
  box-shadow:0 6px 20px rgba(0,0,0,.08), inset 0 1px 0 rgba(255,255,255,.5);
  -webkit-backdrop-filter:blur(16px) saturate(1.4); backdrop-filter:blur(16px) saturate(1.4);
}
.store-link--glass:hover{ box-shadow:0 12px 30px rgba(0,0,0,.14), inset 0 1px 0 rgba(255,255,255,.6); }
.store-link--solid{ background:var(--st-accent); color:#fff; border:1px solid transparent; box-shadow:0 8px 22px color-mix(in srgb, var(--st-accent) 40%, transparent); }
.store-link--solid .store-link__icon{ background:rgba(255,255,255,.2); color:#fff; }
.store-link--solid .store-link__desc{ color:rgba(255,255,255,.8); }
.store-link--outline{ background:transparent; border:1.5px solid color-mix(in srgb, var(--st-accent) 55%, transparent); }
.store-link--outline:hover{ background:color-mix(in srgb, var(--st-accent) 10%, transparent); }
@media (prefers-color-scheme: dark){
  .store-link--glass{ background:color-mix(in srgb, var(--st-card) 62%, transparent); border-color:color-mix(in srgb, #ffffff 12%, transparent); box-shadow:0 6px 20px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.06); }
}

/* Kartu produk */
.store-card{
  display:flex; flex-direction:column; overflow:hidden; border-radius:1.1rem; text-decoration:none; color:var(--st-fg);
  background:color-mix(in srgb, var(--st-card) 70%, transparent);
  border:1px solid color-mix(in srgb, #ffffff 55%, transparent);
  box-shadow:0 6px 20px rgba(0,0,0,.08), inset 0 1px 0 rgba(255,255,255,.5);
  -webkit-backdrop-filter:blur(14px) saturate(1.3); backdrop-filter:blur(14px) saturate(1.3);
  transition:transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s;
}
.store-card:hover{ transform:translateY(-3px); box-shadow:0 16px 36px rgba(0,0,0,.16); }
@media (prefers-color-scheme: dark){
  .store-card{ border-color:color-mix(in srgb, #ffffff 12%, transparent); box-shadow:0 6px 20px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.06); }
}
.store-card__media{ position:relative; aspect-ratio:16/10; background:color-mix(in srgb, var(--st-card) 60%, transparent); overflow:hidden; }
.store-card__title{ color:var(--st-fg); }
.store-badge{ position:absolute; left:.5rem; top:.5rem; border-radius:.4rem; background:color-mix(in srgb, var(--st-card) 88%, transparent); padding:.1rem .45rem; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.03em; color:var(--st-accent); -webkit-backdrop-filter:blur(6px); backdrop-filter:blur(6px); }
.store-discount{ position:absolute; right:.5rem; top:.5rem; border-radius:.4rem; background:#e11d48; padding:.1rem .45rem; font-size:11px; font-weight:800; color:#fff; box-shadow:0 4px 12px rgba(225,29,72,.4); }

/* Media (image/embed) */
.store-media{ background:color-mix(in srgb, var(--st-card) 60%, transparent); border:1px solid color-mix(in srgb, var(--st-fg) 8%, transparent); }

/* Ikon sosial */
.store-social{
  display:flex; align-items:center; justify-content:center; width:2.75rem; height:2.75rem; border-radius:9999px; color:var(--st-fg);
  background:color-mix(in srgb, var(--st-card) 66%, transparent);
  border:1px solid color-mix(in srgb, #ffffff 50%, transparent);
  -webkit-backdrop-filter:blur(12px); backdrop-filter:blur(12px);
  transition:transform .2s, color .2s, box-shadow .2s;
}
.store-social:hover{ transform:translateY(-2px); color:var(--st-accent); box-shadow:0 8px 18px rgba(0,0,0,.14); }
@media (prefers-color-scheme: dark){
  .store-social{ border-color:color-mix(in srgb, #ffffff 12%, transparent); }
}

/* Tombol bagikan melayang */
.store-share{
  position:fixed; z-index:20; right:1.1rem; bottom:calc(1.1rem + env(safe-area-inset-bottom));
  display:inline-flex; align-items:center; gap:.5rem; padding:.7rem .95rem; border-radius:9999px; font-size:.85rem; font-weight:700;
  color:#fff; background:var(--st-accent); border:none; cursor:pointer;
  box-shadow:0 10px 26px color-mix(in srgb, var(--st-accent) 50%, transparent), inset 0 1px 0 rgba(255,255,255,.25);
  transition:transform .2s cubic-bezier(.2,.8,.2,1), box-shadow .2s;
}
.store-share:hover{ transform:translateY(-2px); }
.store-share:active{ transform:scale(.96); }

/* Sembunyikan scrollbar carousel */
.store-carousel{ scrollbar-width:none; }
.store-carousel::-webkit-scrollbar{ display:none; }

/* ── Varian layout: minimal_list — header rata-kiri tanpa kaca ── */
.store-header--flat{
  background:none; border:none; box-shadow:none;
  -webkit-backdrop-filter:none; backdrop-filter:none;
  padding:0;
}
.store-avatar--sm{ width:3.25rem; height:3.25rem; }
.store-root--minimal_list .store-link{ border-radius:.65rem; }

/* ── Varian layout: bio_center — avatar & bio lebih menonjol ── */
.store-avatar--lg{ width:7.5rem; height:7.5rem; }
.store-header--bio{ padding-top:2.25rem; padding-bottom:2.25rem; }

/* ── Varian layout: grid_card — tombol link jadi kartu grid 2 kolom ──
   :has() dipakai untuk membedakan wrapper blok "link" dari tipe lain tanpa
   mengubah struktur .map() di JSX — blok non-link tetap penuh-lebar. */
.store-root--grid_card .store-blocks{ display:grid; grid-template-columns:repeat(2, 1fr); gap:1rem; }
.store-root--grid_card .store-blocks > div{ grid-column:1 / -1; }
.store-root--grid_card .store-blocks > div:has(.store-link){ grid-column:span 1; }
.store-root--grid_card .store-link{ flex-direction:column; align-items:center; text-align:center; gap:.6rem; padding:1.35rem .9rem; }
.store-root--grid_card .store-link__icon{ width:3rem; height:3rem; border-radius:1rem; }
.store-root--grid_card .store-link__desc{ display:none; }
.store-root--grid_card .store-link > svg:last-child{ display:none; }

/* ── Varian layout: mosaic_grid — bento grid: media/produk besar, link/teks kecil.
   Trik :has() sama seperti grid_card di atas, diperluas ke .store-media/.store-card
   supaya blok gambar & produk otomatis dapat sel lebih besar tanpa restrukturisasi JSX. */
.store-root--mosaic_grid .store-blocks{
  display:grid; grid-template-columns:repeat(2, 1fr); grid-auto-flow:dense; gap:.85rem;
}
.store-root--mosaic_grid .store-blocks > div{ grid-column:1 / -1; }
.store-root--mosaic_grid .store-blocks > div:has(.store-link){ grid-column:span 1; }
.store-root--mosaic_grid .store-blocks > div:has(.store-social){ grid-column:1 / -1; }
.store-root--mosaic_grid .store-card__media{ aspect-ratio:1/1; }
@media (min-width:640px){
  .store-root--mosaic_grid .store-blocks{ grid-template-columns:repeat(4, 1fr); }
  .store-root--mosaic_grid .store-blocks > div:has(.store-link){ grid-column:span 2; }
}

/* ── Varian layout: banner_hero — cover penuh-lebar dominan, header menumpuk ── */
.store-root--banner_hero .store-cover{
  aspect-ratio:16/9; border-radius:0; box-shadow:none;
  margin-left:calc(50% - 50vw); margin-right:calc(50% - 50vw); width:100vw; max-width:100vw;
}
.store-root--banner_hero .store-header--cover{ margin-top:-4.5rem; }
.store-root--banner_hero .store-avatar{
  box-shadow:0 0 0 5px var(--st-bg), 0 8px 20px rgba(0,0,0,.22); outline:none;
}

/* ── Gaya latar toko — dibangun dari --st-accent, konsisten dgn .store-bg dasar ── */
.store-bg--gradient{
  background:linear-gradient(135deg, color-mix(in srgb, var(--st-accent) 10%, transparent), color-mix(in srgb, var(--st-accent) 3%, transparent) 60%, transparent);
}
.store-bg--pattern-dots{
  background-image:radial-gradient(color-mix(in srgb, var(--st-accent) 26%, transparent) 1.2px, transparent 1.6px);
  background-size:20px 20px;
}
.store-bg--pattern-grid{
  background-image:
    linear-gradient(color-mix(in srgb, var(--st-accent) 9%, transparent) 1px, transparent 1px),
    linear-gradient(90deg, color-mix(in srgb, var(--st-accent) 9%, transparent) 1px, transparent 1px);
  background-size:26px 26px;
}

/* Animasi masuk */
@keyframes storeIn{ from{ opacity:0; transform:translateY(14px); } to{ opacity:1; transform:none; } }
.store-enter{ animation:storeIn .5s cubic-bezier(.2,.8,.2,1) both; }
@media (prefers-reduced-motion: reduce){
  .store-enter{ animation:none; }
  .store-link, .store-card, .store-social, .store-share{ transition:none; }
}
`;